import { Box, Divider, Link, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import DownloadCSV from 'components/icons/DownloadCSV';
import DownloadPdf from 'components/icons/DownloadPdf';
import DownloadXLS from 'components/icons/DownloadXLS';
import CustomButton from 'components/UI/buttons/CustomButton.style';
import styles from 'features/DefaultHelp.module.css';
import YardCheckStatusChip from 'features/yardCheck/components/YardCheckStatusChip/YardCheckStatusChip';
import { useCallback, useMemo } from 'react';
import { YARD_CHECK_ITEM_TYPE } from 'utils/constants';
const { MISSING, NEW, MATCHED, UPDATED } = YARD_CHECK_ITEM_TYPE;

const CompleteContent = () => {
    const instructions = useMemo(
        () => [
            {
                key: 'remediateItems',
                title: 'Remediate Yard Check Items',
            },
        ],
        []
    );

    const renderOverviewSection = useCallback(
        () => (
            <>
                <h4>OVERVIEW</h4>
                <Typography>
                    Once a Yard Check is marked as <b>Completed</b>, it becomes <b>read-only</b> and no further actions or edits can be made. This
                    view allows users to review the outcomes of the verification process and see the finalized list of checked items and their
                    statuses.
                </Typography>
                <Typography mt={2}>
                    Users can still:
                    <ul className={styles['unordered-list']}>
                        <li>
                            <b>View details</b> of each asset that was reviewed.
                        </li>
                        <li>
                            <b>Export</b> the Yard Check Summary using available export options (PDF, CSV, XLS).
                        </li>
                        <li>
                            <b>Apply</b> filters to review specific types of Yard Check items (e.g., Matched, Missed, etc.).
                        </li>
                    </ul>
                </Typography>
            </>
        ),
        []
    );

    const renderControlsSection = useCallback(
        () => (
            <>
                <h4>CONTROLS</h4>
                <Table className={styles['controls-table']}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Actions</TableCell>
                            <TableCell>Description</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow>
                            <TableCell>
                                <CustomButton customVariant="success" disabled>
                                    Completed
                                </CustomButton>
                            </TableCell>
                            <TableCell>Disabled. No longer applicable once the Yard Check is completed.</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>
                                <CustomButton customVariant="error">Cancel</CustomButton>
                            </TableCell>
                            <TableCell>Exit and return to the Yard Check list</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>Yard Check Filters</TableCell>
                            <TableCell>Toggle the visibility of asset types by status: Missed, Added, Matched, or Updated.</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>
                                <DownloadPdf />
                                <DownloadCSV />
                                <DownloadXLS />
                            </TableCell>
                            <TableCell>Export Yard Check Summary data in PDF, CSV, or XLS format.</TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </>
        ),
        []
    );

    const renderStatusIndicatorSection = useCallback(
        () => (
            <>
                <h4>STATUS INDICATOR</h4>
                <Table className={styles['controls-table']}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Status</TableCell>
                            <TableCell>Description</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow>
                            <TableCell>
                                <YardCheckStatusChip status={MATCHED} />
                            </TableCell>
                            <TableCell>Asset verified and confirmed against inventory.</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>
                                <YardCheckStatusChip status={MISSING} />
                            </TableCell>
                            <TableCell>Asset not found during physical verification.</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>
                                <YardCheckStatusChip status={UPDATED} />
                            </TableCell>
                            <TableCell>Asset found and verified, but information was modified.</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>
                                <YardCheckStatusChip status={NEW} />
                            </TableCell>
                            <TableCell>Asset not in inventory, identified and added during the Yard Check.</TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </>
        ),
        []
    );

    const renderDivider = useCallback(() => <Divider className={styles['divider']} />, []);

    const renderFooter = useCallback(
        () => (
            <Stack className={styles['footer']}>
                <span>
                    {`For further support, email us at `}
                    <Link href="mailto:cs@birdseye.ca" underline="none">
                        cs@birdseye.ca
                    </Link>
                    {'.'}
                </span>
            </Stack>
        ),
        []
    );

    return (
        <Stack className={styles['help-dialog-container']}>
            <Box className={styles['help-dialog-content-container']}>
                {renderOverviewSection()}
                {renderDivider()}
                {renderControlsSection()}
                {renderDivider()}
                {renderStatusIndicatorSection()}
                {renderDivider()}
            </Box>
            {renderFooter()}
        </Stack>
    );
};

export default CompleteContent;
